import React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight, Coins, Flame, ShieldCheck } from 'lucide-react';
import { Card, DataTable, PageHero, PageWrapper, SectionHeader, StatCard } from '../components/UI';
import { Link } from 'react-router-dom';

const allocationData = [
  { name: 'Strategic Round', pct: 10, tokens: '100M', color: '#00e87b', text: 'text-accent-green', bg: 'bg-accent-green' },
  { name: 'Presale', pct: 10, tokens: '100M', color: '#1eadff', text: 'text-accent-blue', bg: 'bg-accent-blue' },
  { name: 'Public Launch', pct: 15, tokens: '150M', color: '#f5a623', text: 'text-accent-amber', bg: 'bg-accent-amber' },
  { name: 'Ecosystem Dev', pct: 15, tokens: '150M', color: '#a78bfa', text: 'text-violet-400', bg: 'bg-violet-400' },
  { name: 'Treasury Reserve', pct: 15, tokens: '150M', color: '#34d399', text: 'text-emerald-400', bg: 'bg-emerald-400' },
  { name: 'Expansion Fund', pct: 15, tokens: '150M', color: '#60a5fa', text: 'text-blue-400', bg: 'bg-blue-400' },
  { name: 'Team & Advisors', pct: 10, tokens: '100M', color: '#fbbf24', text: 'text-amber-300', bg: 'bg-amber-300' },
  { name: 'R&D', pct: 5, tokens: '50M', color: '#f87171', text: 'text-red-400', bg: 'bg-red-400' },
  { name: 'Liquidity Reserve', pct: 5, tokens: '50M', color: '#818cf8', text: 'text-indigo-400', bg: 'bg-indigo-400' },
];

const raisesData = [
  { round: 'Strategic', capital: 500 },
  { round: 'Presale', capital: 1000 },
  { round: 'Public', capital: 2000 },
];

const raisesTable = [
  ['Strategic Round', '100M AZT', '$0.005', '$500,000', 'Phase 1 launch, stations, tech'],
  ['Presale', '100M AZT', '$0.010', '$1,000,000', 'Fleet expansion, BaaS rollout'],
  ['Public Launch', '100M AZT', '$0.020', '$3,000,000', 'National expansion, marketing'],
  ['TOTAL', '300M AZT', '-', '$3,500,000', 'Full launch through national scale'],
];

const Tokenomics: React.FC = () => (
  <>
    <PageHero eyebrow="- AZT TOKENOMICS" title="The Azura Token (AZT)" subtitle="AZT is Azura's platform token, backed by real-world assets, vehicles on the road, battery stations, and operating revenue." accent="var(--accent-amber)" token={true} />

    <PageWrapper>
      <div className="mb-20 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-5">
        <StatCard value="AZT" label="Token Symbol" sub="Azura Token" accent="var(--accent-green)" />
        <StatCard value="1B" label="Total Fixed Supply" sub="No inflation" accent="var(--accent-green)" />
        <StatCard value="$0.005" label="Strategic Round Price" sub="100M tokens available" accent="var(--accent-amber)" />
        <StatCard value="$3.5M" label="Total Raise Target" sub="3 rounds total" accent="var(--accent-blue)" />
      </div>

      <div className="mb-16 relative rounded-[20px] overflow-hidden border border-white/10 aspect-[21/9]">
        <img src="/images/impact.jpg" alt="AZT backed by real-world operations" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <p className="absolute bottom-5 left-6 text-white font-heading text-lg md:text-2xl font-bold max-w-md">AZT is backed by a business already running.</p>
      </div>

      <div className="mb-20 grid gap-8 lg:grid-cols-2">
        <Card>
          <h3 className="mb-6 flex items-center gap-2 font-heading text-xl font-bold"><Coins size={20} className="text-accent-amber" /> Token Parameters</h3>
          {[
            ['Token Name', 'Azura Token'],
            ['Symbol', 'AZT'],
            ['Total Supply', '1,000,000,000 (fixed)'],
            ['Inflation', 'None'],
            ['Token Type', 'Platform Token'],
            ['Token Removal', 'Yes - 10% of revenue permanently removes tokens'],
            ['Launch Year', '2026'],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-white/10 py-2.5">
              <span className="text-sm text-text-muted">{k}</span>
              <span className="max-w-[55%] text-right text-[0.82rem] text-text-primary">{v}</span>
            </div>
          ))}
        </Card>

        <Card className="border-accent-green/20 bg-gradient-to-br from-accent-green/10 to-accent-blue/5">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold"><ShieldCheck size={20} className="text-accent-green" /> Strategic Round Note</h3>
          <div className="mb-3 text-xs tracking-[0.05em] text-accent-green">UPDATED FROM 5% TO 10%</div>
          <p className="mb-4 text-sm leading-7 text-text-secondary">The Strategic Round has been increased from 5% (50M AZT / $250K) to 10% (100M AZT / $500K) at the same price of $0.005 per token.</p>
          <p className="text-sm leading-7 text-text-secondary">This raises $500K vs $250K previously, comfortably covering Phase 1 capex of ~$1.37M when combined with Presale proceeds, plus ~$130K operational buffer.</p>
          <div className="mt-5 rounded-xl border border-accent-green/15 bg-accent-green/10 p-4">
            <p className="text-xs text-accent-green">STRATEGIC ($500K) + PRESALE ($1M) = $1.5M</p>
            <p className="mt-1 text-xs text-text-muted">Covers $1.37M Phase 1 capex with $130K buffer</p>
          </div>
        </Card>
      </div>

      <SectionHeader eyebrow="- TOKEN ALLOCATION" title="Distribution Breakdown" />
      <div className="mb-20 grid items-center gap-8 lg:grid-cols-2">
        <ResponsiveContainer width="100%" height={340}>
          <PieChart>
            <Pie data={allocationData} cx="50%" cy="50%" outerRadius={140} innerRadius={60} paddingAngle={1.5} dataKey="pct">
              {allocationData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
            </Pie>
            <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text-primary)' }} formatter={(value: any, name: any) => [`${value}% - ${allocationData.find((d) => d.name === name)?.tokens} tokens`, name]} />
          </PieChart>
        </ResponsiveContainer>
        <div>
          {allocationData.map((item) => (
            <div key={item.name} className="flex items-center justify-between border-b border-white/10 py-2">
              <div className="flex items-center gap-2.5">
                <div className={`h-2.5 w-2.5 shrink-0 rounded-[3px] ${item.bg}`} />
                <span className="text-[0.85rem] text-text-secondary">{item.name}</span>
              </div>
              <div className="text-right">
                <span className={`text-[0.82rem] font-bold ${item.text}`}>{item.pct}%</span>
                <span className="ml-2 text-xs text-text-muted">{item.tokens}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <SectionHeader eyebrow="- FUNDRAISING ROUNDS" title="Three Rounds. $3.5M Total." />
      <div className="mb-16">
        <div className="mb-8 rounded-[20px] border border-white/10 bg-bg-card p-8">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={raisesData} barSize={60}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="round" stroke="var(--text-muted)" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
              <YAxis stroke="var(--text-muted)" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} tickFormatter={(v) => `$${v}K`} />
              <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text-primary)' }} formatter={(value: any) => [`$${value}K`, 'Capital Raised']} />
              <Bar dataKey="capital" radius={[8, 8, 0, 0]} name="Capital Raised ($K)">
                {raisesData.map((_, i) => <Cell key={i} fill={['#00e87b', '#1eadff', '#f5a623'][i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <DataTable headers={['Round', 'Tokens', 'Price', 'Capital Raised', 'Primary Use']} rows={raisesTable} accentCol={3} />
      </div>

      <Card className="mb-16 border-accent-amber/20 bg-gradient-to-br from-accent-amber/10 to-transparent text-center">
        <h3 className="mb-2 font-heading text-2xl font-bold text-text-primary">Strategic Round opens <span className="text-accent-amber">October 15.</span></h3>
        <p className="mb-6 text-sm leading-6 text-text-secondary max-w-xl mx-auto">100M AZT at $0.005 each. Get in before the Presale price doubles.</p>
        <Link to="/invest" className="inline-flex items-center gap-2 rounded-xl bg-accent-amber px-7 py-3.5 font-heading text-[0.95rem] font-bold text-bg-dark shadow-[0_0_30px_rgba(255,184,0,0.3)] transition hover:-translate-y-1">
          Invest Now <ArrowRight size={18} />
        </Link>
      </Card>

      <SectionHeader eyebrow="- TOKEN UTILITY" title="What AZT Does" />
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-5">
        {[
          ['Raising Funds', "Enables community and strategic investors to participate in Azura's growth during early rounds."],
          ['Reducing Token Supply', '10% of all operating revenue is used to permanently remove tokens from circulation, gradually reducing the total tokens available.'],
          ['Platform Participation', 'AZT holders gain access to platform benefits, updates, and a voice in community decisions as the platform grows.'],
          ['Long-Term Alignment', 'Aligns supporters, strategic partners, and the company around shared long-term success.'],
          ['Real-World Backing', 'AZT is backed by real-world assets: vehicles on the road, battery stations, and operating revenue.'],
          ['Blockchain Transparency', "Blockchain technology makes AZT's management transparent and makes it easy to take part in the platform."],
        ].map(([title, desc]) => (
          <Card key={title}>
            <h4 className="mb-2 flex items-center gap-2 font-heading text-[0.95rem] font-bold text-accent-amber"><Flame size={16} /> {title}</h4>
            <p className="text-sm leading-6 text-text-secondary">{desc}</p>
          </Card>
        ))}

        <Link to="/opportunity" className="inline-flex items-center gap-2 mt-4 rounded-xl bg-accent-green px-7 py-3.5 font-heading text-[0.95rem] font-bold text-bg-primary shadow-[0_0_30px_rgba(0,232,123,0.3)] transition hover:-translate-y-1">
          Get AZT Token <ArrowRight size={18} />
        </Link>

      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="relative rounded-2xl overflow-hidden aspect-video border border-white/10">
          <img src="/images/bike4.png" alt="EVs on the road" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <p className="absolute bottom-3 left-4 text-white text-xs uppercase tracking-widest">EVs on the road</p>
        </div>
        <div className="relative rounded-2xl overflow-hidden aspect-video border border-white/10">
          <img src="/images/bg4.jpg" alt="Battery-swap stations" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <p className="absolute bottom-3 left-4 text-white text-xs uppercase tracking-widest">Battery-swap stations</p>
        </div>
      </div>

      <Card className="mt-8 border-white/10">
        <h4 className="mb-1.5 font-heading text-[0.95rem] font-bold text-text-primary">Prefer not to use crypto?</h4>
        <p className="mb-4 text-sm leading-6 text-text-secondary">You can still take part in the Strategic or Presale round without needing a crypto wallet. Reach our investor relations team directly and we'll walk you through it.</p>
        <div className="flex flex-wrap gap-3">
          <a href="mailto:invest@azuramobility.com" className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 py-2.5 text-sm font-bold text-text-primary transition hover:border-accent-amber hover:text-accent-amber">
            invest@azuramobility.com
          </a>
          <a href="https://t.me/azuramobility" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 py-2.5 text-sm font-bold text-text-primary transition hover:border-accent-amber hover:text-accent-amber">
            Message us on Telegram
          </a>
        </div>
      </Card>
    </PageWrapper>
  </>
);

export default Tokenomics;
